/**
 * Checkout Context - FIXED FLOW FOR FIVEM STORES
 * 
 * CRITICAL: FiveM/Tebex stores ALWAYS require authentication before adding packages!
 * The 422 error occurs when trying to add packages to an unauthenticated basket.
 * 
 * CORRECT Flow:
 * 1. Create basket
 * 2. ALWAYS redirect to auth (FiveM stores require it)
 * 3. After auth return -> add packages -> launch overlay
 * 
 * NOTE: Do NOT remove tebex_pending until AFTER the flow completes to handle page reloads
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { useCart } from './CartContext';
import {
  createBasket,
  checkBasketAuth,
  addPackageToBasket,
  removePackageFromBasket,
  getBasket,
  clearBasketStorage,
} from '@/services/TebexService';
import { buildEcommerceItem, trackEcommerceEvent, getSafeCurrencyFromItem, getSafePriceFromItem } from '@/utils/analytics';
import {
  validateRetroFootballCart
} from '@/data/retroFootball';
import {
  activateFootballClaim,
  clearActiveFootballClaim,
  clearCheckoutContinuation,
  createFootballClaim,
  getCheckoutContinuation,
  getPackageCustomData,
  storeCheckoutContinuation
} from '@/services/FootballFulfillmentService';

const CheckoutContext = createContext();
const PENDING_CHECKOUT_MAX_AGE_MS = 30 * 60 * 1000;

const createCheckoutItemSnapshot = (cartItemsInput) => {
  const sourceItems = Array.isArray(cartItemsInput)
    ? cartItemsInput
    : [{ id: cartItemsInput, quantity: 1 }];

  return Object.freeze(sourceItems.map((item) => {
    const id = String(item?.tebexPackageId || item?.id || '');
    const quantity = Number(item?.quantity ?? item?.qty ?? 1);
    return Object.freeze({
      id,
      tebexPackageId: id,
      qty: quantity,
      quantity,
      giftToId: item?.giftToId || null,
      giftTo: item?.giftTo || null,
      title: item?.title || item?.name || `Package #${id}`,
      name: item?.name || item?.title || `Package #${id}`,
      basePrice: item?.basePrice,
      price: item?.price,
      currency: item?.currency
    });
  }));
};

const getAddPackageOptions = (item, claimToken) => ({
  targetUsername: item.giftTo || null,
  custom: getPackageCustomData(item.id, claimToken)
});

export const useCheckout = () => {
  const context = useContext(CheckoutContext);
  if (!context) {
    throw new Error('useCheckout must be used within a CheckoutProvider');
  }
  return context;
};

export const CHECKOUT_STATES = { IDLE: 'idle', LOADING: 'loading', ERROR: 'error' };

export const CheckoutProvider = ({ children }) => {
  const { clearCart } = useCart();
  
  // Initialize user from localStorage
  const [user, setUser] = useState(() => {
    try {
      const savedUser = localStorage.getItem('tebex_user');
      return savedUser ? JSON.parse(savedUser) : null;
    } catch {
      localStorage.removeItem('tebex_user');
      return null;
    }
  });
  
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    try {
      const savedUser = localStorage.getItem('tebex_user');
      return savedUser ? !!JSON.parse(savedUser) : false;
    } catch {
      return false;
    }
  });
  
  const [checkoutState, setCheckoutState] = useState(CHECKOUT_STATES.IDLE);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showLoadingOverlay, setShowLoadingOverlay] = useState(false);
  
  // Legacy state
  const [basket, setBasket] = useState(null);
  const [checkoutBasketIdent, setCheckoutBasketIdent] = useState(null);
  const [authLinks, setAuthLinks] = useState([]);
  const [pendingPackages, setPendingPackages] = useState([]);
  const [authMode, setAuthMode] = useState(null);
  const [isCheckoutModalOpen, setIsCheckoutModalOpen] = useState(false);
  const [isAuthWidgetOpen, setIsAuthWidgetOpen] = useState(false);
  const [authBasketIdent, setAuthBasketIdent] = useState(null);
  
  const checkoutInProgressRef = useRef(false);
  const hasProcessedAuthReturn = useRef(false); // Prevent double processing
  
  /**
   * Prompt user to connect Discord before checkout
   * Uses event-based communication with DiscordContext
   * 
   * @param {string} basketIdent - Basket ident for checkout continuation
   * @returns {Promise<{connected: boolean, skipped: boolean}>}
   */
  const promptDiscordConnection = useCallback((basketIdent) => {
    return new Promise((resolve) => {
      window.dispatchEvent(new CustomEvent('discord-prompt-request', {
        detail: { basketIdent, resolve }
      }));
    });
  }, []);

  const resetState = useCallback(() => {
    checkoutInProgressRef.current = false;
    setCheckoutState(CHECKOUT_STATES.IDLE);
    setError(null);
    setIsLoading(false);
    setShowLoadingOverlay(false);
  }, []);

  const trackBeginCheckout = useCallback((itemsForCheckout = []) => {
    if (!itemsForCheckout.length) return;

    const currency = getSafeCurrencyFromItem(itemsForCheckout[0]);
    const value = itemsForCheckout.reduce(
      (total, item) => total + (getSafePriceFromItem(item) * (item.quantity || 1)),
      0
    );

    trackEcommerceEvent('begin_checkout', {
      currency,
      value,
      items: itemsForCheckout.map((item) => buildEcommerceItem(item, item.quantity || 1))
    });
  }, []);

  /**
   * Launch Tebex overlay
   */
  const launchTebexOverlay = useCallback((
    basketIdent,
    _itemsForSurvey = [],
    claimToken = null
  ) => {
    if (claimToken && !activateFootballClaim(claimToken)) {
      return Promise.reject(
        new Error('Your delivery session expired. Please restart checkout.')
      );
    }
    
    return new Promise((resolve, reject) => {
      // Wait for Tebex.js to be available
      const waitForTebex = async (attempts = 0) => {
        if (typeof window.Tebex !== 'undefined' && window.Tebex.checkout) {
          launchOverlay();
          return;
        }

        if (attempts === 0 && typeof window.__loadTebexScript === 'function') {
          try {
            await window.__loadTebexScript();
          } catch (e) {
            console.error('[Checkout] Failed loading Tebex script:', e);
          }
        }

        if (attempts < 50) { // Wait up to 5 seconds
          setTimeout(() => waitForTebex(attempts + 1), 100);
        } else {
          console.error('[Checkout] Tebex.js not available after waiting');
          reject(new Error('Tebex.js not loaded'));
        }
      };
      
      const launchOverlay = () => {
        // CRITICAL: Use destroy() instead of close() to fully reset Tebex state
        // close() keeps the internal Zoid component alive, causing re-init to silently fail
        try { window.Tebex.checkout.destroy(); } catch (e) { /* ignore */ }
        
        // Small delay to let Tebex fully clean up before re-initializing
        setTimeout(() => {
          try {
            window.Tebex.checkout.off('payment:complete');
            window.Tebex.checkout.off('close');
            window.Tebex.checkout.off('payment:error');
          } catch (e) { /* ignore */ }
          
          window.Tebex.checkout.on('payment:complete', () => {
            // Tebex's browser event is only a navigation signal. The signed
            // payment.completed webhook remains the sole fulfillment authority.
            window.location.href = '/checkout/success';
          });
          
          window.Tebex.checkout.on('close', () => {
            localStorage.removeItem('tebex_basket_ident');
            clearCheckoutContinuation();
            resetState();
            resolve('closed');
          });
          
          window.Tebex.checkout.on('payment:error', () => {
            console.error('[Checkout] Tebex reported a payment error');
            resetState();
            reject(new Error('Payment could not be completed.'));
          });
          
          window.Tebex.checkout.init({ ident: basketIdent, theme: 'dark' });
          window.Tebex.checkout.launch();
        }, 100);
      };
      
      waitForTebex();
    });
  }, [resetState]);

  /**
   * RESUME AFTER AUTH RETURN
   * 
   * This is called when user returns from FiveM authentication.
   * For checkout: add packages AFTER auth, then launch overlay
   * For login: just update user state
   * 
   * CRITICAL: Do NOT remove tebex_pending until process completes!
   * 
   * NOTE: This must be defined BEFORE initiateCheckout and loginWithFiveM
   * because they reference it in their dependency arrays.
   */
  const resumeCheckoutAfterAuth = useCallback(async () => {
    
    // Prevent double processing (React strict mode, fast refresh, etc.)
    if (hasProcessedAuthReturn.current) {
      return null;
    }
    
    const pendingStr = localStorage.getItem('tebex_pending');
    
    if (!pendingStr) {
      // Check if user is already authenticated
      const basketIdent = localStorage.getItem('tebex_basket_ident');
      if (basketIdent) {
        // Try to get user info from basket
        try {
          const basket = await getBasket(basketIdent);
          const username = basket?.username || basket?.username_id;
          if (username) {
            localStorage.setItem('tebex_user', JSON.stringify({ authenticated: true, username }));
            setUser({ authenticated: true, username });
            setIsAuthenticated(true);
            window.dispatchEvent(new CustomEvent('tebex-user-updated'));
          }
        } catch (e) {
          console.error('[AuthReturn] Failed to get basket:', e);
        }
      }
      return null;
    }
    
    // Mark as processing
    hasProcessedAuthReturn.current = true;
    
    let pending;
    try {
      pending = JSON.parse(pendingStr);
    } catch (e) {
      console.error('[AuthReturn] Failed to parse pending data:', e);
      localStorage.removeItem('tebex_pending');
      hasProcessedAuthReturn.current = false;
      return null;
    }

    const pendingTimestamp = Number(pending?.timestamp);
    const pendingAge = Date.now() - pendingTimestamp;
    if (
      !Number.isFinite(pendingTimestamp) ||
      pendingAge < -60 * 1000 ||
      pendingAge > PENDING_CHECKOUT_MAX_AGE_MS
    ) {
      localStorage.removeItem('tebex_pending');
      localStorage.removeItem('tebex_basket_ident');
      clearCheckoutContinuation();
      hasProcessedAuthReturn.current = false;
      return {
        type: 'error',
        error: 'Your checkout session expired. Please start again.',
        returnUrl: pending?.returnUrl || '/'
      };
    }
    
    const { type, basketIdent, items, returnUrl } = pending;
    let claimToken = pending.claimToken || null;
    
    // Step 1: Get user info from the authenticated basket
    try {
      const basket = await getBasket(basketIdent);
      const username = basket?.username || basket?.username_id || 'FiveM User';
      localStorage.setItem('tebex_user', JSON.stringify({ authenticated: true, username }));
      setUser({ authenticated: true, username });
      setIsAuthenticated(true);
      window.dispatchEvent(new CustomEvent('tebex-user-updated'));
    } catch (e) {
      console.error('[AuthReturn] Failed to get user from basket:', e);
      localStorage.setItem('tebex_user', JSON.stringify({ authenticated: true, username: 'FiveM User' }));
      setUser({ authenticated: true, username: 'FiveM User' });
      setIsAuthenticated(true);
    }
    
    // Step 2: Handle based on type
    if (type === 'login') {
      // Store authenticated basket for future checkout reuse (basket is empty, perfect for reuse)
      localStorage.setItem('tebex_auth_basket', basketIdent);
      console.log('[AuthReturn] Stored authenticated basket for future use:', basketIdent);
      
      // Only clear pending data AFTER successful login
      localStorage.removeItem('tebex_pending');
      hasProcessedAuthReturn.current = false;
      return { type: 'login', returnUrl: returnUrl || '/' };
    }
    
    if (type === 'checkout' && items && items.length > 0) {
      const cartValidation = validateRetroFootballCart(items);
      if (!cartValidation.valid) {
        localStorage.removeItem('tebex_pending');
        hasProcessedAuthReturn.current = false;
        return {
          type: 'error',
          error: cartValidation.message,
          returnUrl
        };
      }

      if (cartValidation.hasIap) {
        const restoredClaim = claimToken
          ? activateFootballClaim(claimToken)
          : null;
        if (!restoredClaim) {
          claimToken = createFootballClaim(items)?.token || null;
        }
        if (!claimToken) {
          localStorage.removeItem('tebex_pending');
          hasProcessedAuthReturn.current = false;
          return {
            type: 'error',
            error: 'A secure delivery session could not be created.',
            returnUrl
          };
        }
      } else {
        claimToken = null;
        clearActiveFootballClaim();
      }

      // NOW add packages (basket is authenticated!)
      try {
        for (const item of items) {
          const targetUsername = item.giftTo || null;

          // Add package with proper gifting support
          // targetUsername is null for self-purchases, username for gifts
          await addPackageToBasket(
            basketIdent,
            item.id,
            item.qty,
            getAddPackageOptions(item, claimToken)
          );

          if (targetUsername) {
            console.log('[AuthReturn] Added GIFT package:', item.id);
          } else {
            console.log('[AuthReturn] Added SELF-PURCHASE package:', item.id);
          }
        }
        
        // Store authenticated basket for future reuse (if user closes overlay and tries again)
        localStorage.setItem('tebex_auth_basket', basketIdent);
        console.log('[AuthReturn] Stored authenticated basket for future use:', basketIdent);
        
        // Only clear pending data AFTER packages are added
        localStorage.removeItem('tebex_pending');
        hasProcessedAuthReturn.current = false;

        storeCheckoutContinuation({
          basketIdent,
          items,
          claimToken
        });
        
        return {
          type: 'checkout',
          basketIdent,
          items,
          claimToken,
          returnUrl: returnUrl || '/'
        };
        
      } catch (e) {
        console.error('[AuthReturn] Failed to add packages:', e);
        localStorage.removeItem('tebex_pending');
        hasProcessedAuthReturn.current = false;
        return { type: 'error', error: e.message, returnUrl };
      }
    }
    
    // Unknown type or no items
    localStorage.removeItem('tebex_pending');
    hasProcessedAuthReturn.current = false;
    return null;
  }, []);

  /**
   * PAY WITH TEBEX
   * 
   * OPTIMIZED FLOW FOR FIVEM STORES:
   * FiveM stores require authentication before packages can be added.
   * The 422 error happens when trying to add packages to an unauthenticated basket.
   * 
   * Flow:
   * 1. Check for existing authenticated basket (FAST PATH - no redirect)
   * 2. If no valid basket, create new one and redirect to auth (SLOW PATH)
   * 
   * KEY INSIGHT: Tebex doesn't maintain cross-basket auth. Each new basket starts
   * unauthenticated. To skip auth redirect, we must REUSE an authenticated basket.
   */
  const initiateCheckout = useCallback(async (cartItemsInput) => {
    
    // Prevent double clicks
    if (checkoutInProgressRef.current) {
      return;
    }

    const items = createCheckoutItemSnapshot(cartItemsInput);
    const cartValidation = validateRetroFootballCart(items);
    if (!cartValidation.valid) {
      setError(cartValidation.message);
      toast.error(cartValidation.message);
      return;
    }

    const claim = cartValidation.hasIap
      ? createFootballClaim(items)
      : null;
    const claimToken = claim?.token || null;
    if (!cartValidation.hasIap) {
      clearActiveFootballClaim();
    }
    
    checkoutInProgressRef.current = true;
    setIsLoading(true);
    setShowLoadingOverlay(true);
    setError(null);
    
    
    try {
      // FAST PATH: Try to reuse existing authenticated basket
      const storedAuthBasket = localStorage.getItem('tebex_auth_basket');
      
      if (storedAuthBasket && isAuthenticated) {
        try {
          console.log('[Checkout] Checking stored authenticated basket:', storedAuthBasket);
          const existingBasket = await getBasket(storedAuthBasket);
          
          // Basket is valid if it exists and is not completed
          if (existingBasket && existingBasket.complete === false) {
            console.log('[Checkout] Reusing authenticated basket - skipping auth redirect');

            if (!Array.isArray(existingBasket.packages)) {
              throw new Error('Authenticated basket contents could not be verified.');
            }
            
            // Clear any existing packages first (from previous cancelled checkout)
            if (existingBasket.packages.length > 0) {
              console.log('[Checkout] Clearing', existingBasket.packages.length, 'existing packages from basket');
              for (const pkg of existingBasket.packages) {
                await removePackageFromBasket(storedAuthBasket, pkg.id);
              }

              const emptiedBasket = await getBasket(storedAuthBasket);
              if (
                !Array.isArray(emptiedBasket?.packages) ||
                emptiedBasket.packages.length !== 0
              ) {
                throw new Error('Authenticated basket could not be emptied safely.');
              }
            }
            
            // Add new packages to authenticated basket
            for (const item of items) {
              await addPackageToBasket(
                storedAuthBasket,
                item.id,
                item.qty,
                getAddPackageOptions(item, claimToken)
              );
            }

            storeCheckoutContinuation({
              basketIdent: storedAuthBasket,
              items,
              claimToken
            });
            
            // Prompt for Discord connection before launching Tebex
            setShowLoadingOverlay(false);
            const discordResult = await promptDiscordConnection(storedAuthBasket);
            
            // If user clicked "Connect Discord", they'll be redirected
            // The flow will continue via discord-checkout-continue event
            if (!discordResult.connected && !discordResult.skipped) {
              // User is being redirected to Discord OAuth
              return;
            }
            
            // User skipped or already connected - launch Tebex overlay
            trackBeginCheckout(items);
            const overlayResult = await launchTebexOverlay(
              storedAuthBasket,
              items,
              claimToken
            );
            
            // Only clear stored basket on successful payment
            // Keep it for reuse if user just closed/cancelled
            if (overlayResult === 'complete') {
              localStorage.removeItem('tebex_auth_basket');
            }
            resetState();
            return;
          } else {
            console.log('[Checkout] Stored basket is completed or invalid, creating new one');
            localStorage.removeItem('tebex_auth_basket');
          }
        } catch (reuseError) {
          console.log('[Checkout] Could not reuse stored basket:', reuseError.message);
          localStorage.removeItem('tebex_auth_basket');
          // Fall through to create new basket
        }
      }
      
      // SLOW PATH: Create new basket and authenticate
      console.log('[Checkout] Creating new basket (no valid authenticated basket found)');
      const newBasket = await createBasket();
      const basketIdent = newBasket.ident;
      
      // Check if auth is needed (will almost always be true for new baskets)
      const authStatus = await checkBasketAuth(basketIdent);
      
      // Unlikely but handle case where new basket doesn't need auth
      if (authStatus.complete) {
        console.log('[Checkout] New basket already authenticated (unexpected but handling)');
        
        for (const item of items) {
          await addPackageToBasket(
            basketIdent,
            item.id,
            item.qty,
            getAddPackageOptions(item, claimToken)
          );
        }

        storeCheckoutContinuation({
          basketIdent,
          items,
          claimToken
        });
        
        setShowLoadingOverlay(false);
        trackBeginCheckout(items);
        await launchTebexOverlay(basketIdent, items, claimToken);
        resetState();
        return;
      }
      
      // Auth required - redirect to CFX.re
      const authUrl = authStatus.links?.[0]?.url;
      
      if (!authUrl) {
        console.error('[Checkout] No auth URL returned');
        setError('Authentication required but no auth URL received');
        resetState();
        return;
      }
      
      // Save pending checkout data and redirect to auth
      const pendingData = {
        type: 'checkout',
        basketIdent,
        items, // Items to add AFTER auth completes (now includes gift info)
        claimToken,
        returnUrl: window.location.pathname,
        timestamp: Date.now()
      };
      
      localStorage.setItem('tebex_basket_ident', basketIdent);
      localStorage.setItem('tebex_pending', JSON.stringify(pendingData));
      
      // Redirect to FiveM authentication
      window.location.href = authUrl;
      
    } catch (err) {
      console.error('[Checkout] ERROR:', err);
      setError(err.message);
      resetState();
    }
  }, [resetState, launchTebexOverlay, isAuthenticated, promptDiscordConnection, trackBeginCheckout]);

  /**
   * LOGIN WITH FIVEM (separate from checkout)
   * Only handles authentication, doesn't add packages
   */
  const loginWithFiveM = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Create a basket just for authentication
      const newBasket = await createBasket();
      const basketIdent = newBasket.ident;
      
      // Check if auth is actually needed
      const authStatus = await checkBasketAuth(basketIdent);
      
      // FAST PATH: Already logged in
      if (authStatus.complete) {
        console.log('[Login] User already authenticated');
        try {
          const basket = await getBasket(basketIdent);
          const username = basket?.username || basket?.username_id || 'FiveM User';
          localStorage.setItem('tebex_user', JSON.stringify({ authenticated: true, username }));
          // Store authenticated basket for future checkout reuse
          localStorage.setItem('tebex_auth_basket', basketIdent);
          setUser({ authenticated: true, username });
          setIsAuthenticated(true);
          window.dispatchEvent(new CustomEvent('tebex-user-updated'));
        } catch (_e) {
          // Fallback if can't get username
          localStorage.setItem('tebex_user', JSON.stringify({ authenticated: true, username: 'FiveM User' }));
          localStorage.setItem('tebex_auth_basket', basketIdent);
          setUser({ authenticated: true, username: 'FiveM User' });
          setIsAuthenticated(true);
        }
        setIsLoading(false);
        
        // Show Discord modal after login (simple show, no promise needed)
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('discord-show-modal'));
        }, 500);
        
        return;
      }
      
      // SLOW PATH: Auth required - redirect
      const authUrl = authStatus.links?.[0]?.url;
      
      if (!authUrl) {
        console.error('[Login] No auth URL received');
        setError('Could not get authentication URL');
        setIsLoading(false);
        return;
      }
      
      // Save pending login data
      const pendingData = {
        type: 'login', // NOT checkout - just login
        basketIdent,
        returnUrl: window.location.pathname,
        timestamp: Date.now()
      };
      
      localStorage.setItem('tebex_basket_ident', basketIdent);
      localStorage.setItem('tebex_pending', JSON.stringify(pendingData));
      
      // Redirect to FiveM authentication
      window.location.href = authUrl;
      
    } catch (err) {
      console.error('[Login] Error:', err);
      setError(err.message);
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    setIsAuthenticated(false);
    clearCart();
    localStorage.removeItem('tebex_user');
    localStorage.removeItem('tebex_basket_ident');
    localStorage.removeItem('tebex_pending');
    localStorage.removeItem('tebex_auth_basket');
    clearActiveFootballClaim();
    clearCheckoutContinuation();
    clearBasketStorage();
    resetState();
  }, [resetState, clearCart]);

  // Legacy
  const resetCheckout = resetState;
  const closeAuthWidget = useCallback(() => setIsAuthWidgetOpen(false), []);
  const handleCfxAuth = useCallback(() => {}, []);
  const redirectToCheckout = useCallback(() => {}, []);
  const initiateGiftCheckout = useCallback(async (packageId, qty, recipient) => {
    const giftTo = typeof recipient === 'string'
      ? recipient
      : recipient?.username || null;
    await initiateCheckout([{
      tebexPackageId: packageId,
      quantity: qty,
      giftTo
    }]);
  }, [initiateCheckout]);

  /**
   * AUTO-PROCESS PENDING AUTH ON MOUNT
   * 
   * When user returns from CFX.re auth, they're redirected to the original page.
   * This effect detects pending auth data and processes it automatically,
   * showing only a brief loading overlay instead of a separate "Completing auth" page.
   */
  useEffect(() => {
    const processPendingAuth = async () => {
      // Check if there's pending auth to process
      const pendingStr = localStorage.getItem('tebex_pending');
      if (!pendingStr) return;
      
      // Don't process if already handled
      if (hasProcessedAuthReturn.current) return;
      
      console.log('[CheckoutProvider] Detected pending auth, processing...');
      setShowLoadingOverlay(true);
      
      try {
        const result = await resumeCheckoutAfterAuth();
        
        if (!result) {
          console.log('[CheckoutProvider] No pending auth result');
          setShowLoadingOverlay(false);
          return;
        }
        
        if (result.type === 'error') {
          console.error('[CheckoutProvider] Auth error:', result.error);
          setShowLoadingOverlay(false);
          setError(result.error);
          return;
        }
        
        if (result.type === 'login') {
          console.log('[CheckoutProvider] Login completed, prompting Discord');
          setShowLoadingOverlay(false);
          
          // Show Discord modal after login (simple show, no promise needed)
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('discord-show-modal'));
          }, 500);
          
          return;
        }
        
        if (result.type === 'checkout') {
          console.log('[CheckoutProvider] Checkout auth completed, prompting Discord');
          
          // Small delay to let the page render
          await new Promise(resolve => setTimeout(resolve, 100));
          
          setShowLoadingOverlay(false);
          
          // Prompt for Discord connection before launching Tebex
          const discordResult = await promptDiscordConnection(result.basketIdent);
          
          // If user clicked "Connect Discord", they'll be redirected
          // The flow will continue via discord-checkout-continue event
          if (!discordResult.connected && !discordResult.skipped) {
            return;
          }
          
          // User skipped or already connected - launch Tebex overlay
          trackBeginCheckout(result.items || []);
          await launchTebexOverlay(
            result.basketIdent,
            result.items || [],
            result.claimToken || null
          );
        }
        
      } catch (err) {
        console.error('[CheckoutProvider] Error processing pending auth:', err);
        setShowLoadingOverlay(false);
        setError(err.message);
      }
    };
    
    // Run after a small delay to let the page settle
    const timer = setTimeout(processPendingAuth, 100);
    return () => clearTimeout(timer);
  }, [resumeCheckoutAfterAuth, launchTebexOverlay, promptDiscordConnection, trackBeginCheckout]);

  /**
   * Listen for Discord OAuth completion to continue checkout
   * This handles the case where user connected Discord during checkout flow
   */
  useEffect(() => {
    const handleDiscordContinue = async (event) => {
      const { basketIdent } = event.detail;
      console.log('[CheckoutProvider] Continuing checkout after Discord connection');
      
      if (basketIdent) {
        const continuation = getCheckoutContinuation(basketIdent);
        if (!continuation?.items?.length) {
          const message = 'Your checkout session expired. Please start again.';
          localStorage.removeItem('tebex_auth_basket');
          clearCheckoutContinuation();
          resetState();
          setError(message);
          toast.error(message);
          return;
        }

        const checkoutItems = continuation.items;
        const validation = validateRetroFootballCart(checkoutItems);
        const claimIsRecoverable = !validation.hasIap || (
          continuation.claimToken &&
          activateFootballClaim(continuation.claimToken)
        );
        if (!validation.valid || !claimIsRecoverable) {
          const message = !validation.valid
            ? validation.message
            : 'Your secure delivery session expired. Please start again.';
          localStorage.removeItem('tebex_auth_basket');
          clearCheckoutContinuation();
          resetState();
          setError(message);
          toast.error(message);
          return;
        }

        trackBeginCheckout(checkoutItems);
        await launchTebexOverlay(
          basketIdent,
          checkoutItems,
          continuation.claimToken || null
        );
      }
    };
    
    window.addEventListener('discord-checkout-continue', handleDiscordContinue);
    return () => window.removeEventListener('discord-checkout-continue', handleDiscordContinue);
  }, [launchTebexOverlay, resetState, trackBeginCheckout]);

  const value = {
    user, isAuthenticated, basketIdent: checkoutBasketIdent, basket,
    checkoutState, error, isLoading, showLoadingOverlay,
    authLinks, pendingPackages, authMode, isCheckoutModalOpen,
    isAuthWidgetOpen, authBasketIdent,
    initiateCheckout, initiateGiftCheckout, loginWithFiveM, logout,
    resetCheckout, closeAuthWidget, handleCfxAuth, redirectToCheckout,
    setIsCheckoutModalOpen, resumeCheckoutAfterAuth, launchTebexOverlay,
    CHECKOUT_STATES
  };

  return (
    <CheckoutContext.Provider value={value}>
      {children}
    </CheckoutContext.Provider>
  );
};

export default CheckoutContext;
